import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../../components/ui';
import { Users, Wallet, ShoppingCart, ClipboardList, Package, BarChart3 } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';

/** Panel de la Mini Banca: resumen de su ecosistema interno. */

interface MiInfo {
  nombre: string;
  vendedores_activos: number;
  vendedores_total: number;
}

interface SaldosInternos {
  total_consolidado: string;
}

const n = (v: unknown): number => {
  const x = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(x) ? x : 0;
};

export const MiniBancaDashboardPage: React.FC = () => {
  const [info, setInfo] = useState<MiInfo | null>(null);
  const [saldoTotal, setSaldoTotal] = useState<string>('0');
  const [ventasHoy, setVentasHoy] = useState(0);

  useEffect(() => {
    fetchAPI<MiInfo>('/usuarios/minibanca/mi-info').then(setInfo).catch(() => setInfo(null));
    fetchAPI<SaldosInternos>('/saldos/minibanca/vendedores')
      .then(d => setSaldoTotal(d.total_consolidado))
      .catch(() => setSaldoTotal('0'));
    fetchAPI<any>(`/reportes/por-sorteo?fecha=${fechaLocalYMD()}`)
      .then(body => {
        const rows = Array.isArray(body) ? body : (body?.reportes ?? body?.sorteos ?? []);
        setVentasHoy(rows.reduce((a: number, r: any) => a + n(r.total_vendido), 0));
      })
      .catch(() => setVentasHoy(0));
  }, []);

  const accesos = [
    { label: 'POS — Vender', path: '/local/pos', icon: <ShoppingCart size={20} /> },
    { label: 'Mis vendedores', path: '/minibanca/vendedores', icon: <Users size={20} /> },
    { label: 'Paquetes internos', path: '/minibanca/paquetes', icon: <Package size={20} /> },
    { label: 'Listas de vendedores', path: '/minibanca/listas', icon: <ClipboardList size={20} /> },
    { label: 'Reportes internos', path: '/minibanca/reportes', icon: <BarChart3 size={20} /> },
    { label: 'Saldos de vendedores', path: '/minibanca/saldos', icon: <Wallet size={20} /> },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {info?.nombre || 'Mi Mini Banca'}
        </h1>
        <p className="text-gray-500">Panel de su ecosistema interno.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <p className="text-sm text-gray-500">Vendedores internos</p>
          <p className="text-2xl font-bold">
            {info ? `${info.vendedores_activos} activos / ${info.vendedores_total}` : '—'}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Ventas de hoy (consolidado)</p>
          <p className="text-2xl font-bold">₡{ventasHoy.toLocaleString('es-CR')}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Saldo consolidado interno</p>
          <p className="text-2xl font-bold">₡{n(saldoTotal).toLocaleString('es-CR')}</p>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {accesos.map(a => (
          <Link
            key={a.path}
            to={a.path}
            className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition text-gray-700 font-medium"
          >
            {a.icon}
            {a.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default MiniBancaDashboardPage;
